import { ClerkLoaded, ClerkProvider } from "@clerk/clerk-expo";
import { BottomSheetModalProvider } from "@gorhom/bottom-sheet";
import { ThemeProvider as NavigationThemeProvider } from "@react-navigation/native";
import * as SentryNative from "@sentry/react-native";
import { Stack } from "expo-router";
import Head from "expo-router/head";
import * as SecureStore from "expo-secure-store";
import { StatusBar } from "expo-status-bar";
import type React from "react";
import { LogBox } from "react-native";
import { PaperProvider } from "react-native-paper";
import type { ThemeProp } from "react-native-paper/lib/typescript/types";

import "../global.css";

/* ----------------- UI / Theme ----------------- */
import theme from "@configs/theme";
import { ThemeContextProvider, useThemeContext } from "@contexts/ThemeContext";

import { buildType } from "@helpers/expo-extras";
import { getClerkPublishableKey } from "@helpers/find-onboarding";
import { initSentry } from "@helpers/lib/sentry";
import { isWeb } from "@helpers/platform";
import { useAppStore } from "@hooks/useAppStore";
import useAuthUser from "@hooks/useAuthUser";
import useExams from "@hooks/useExams";
import { useProductDetails, useProductMetadata } from "@hooks/useProduct";
import { applyGlobalPolyfills } from "@memorang/helpers/applyGlobalPolyfills";
import { identifyUser, initializeAnalytics } from "analytics";
/* ----------------- Helpers / Hooks ----------------- */
import useCachedResources from "../src/hooks/useCachedResources";

import { HapticProvider } from "@contexts/HapticContexts";
/* ----------------- Providers ----------------- */
import ReactQueryProvider from "@contexts/ReactQueryContext";
import { AuthProvider } from "@features/auth/contexts/AuthContext";
import { ConsentProvider } from "@features/auth/contexts/ConsentProvider";
import { ExamContextProvider } from "@features/exam/contexts/ExamContext";
import { getAuthToken } from "@helpers/auth-provider";
import { TTSProvider } from "@memorang/features/src/TTS/TTSContext";
import { ToastProvider } from "@memorang/ui/components/Toast";
import { AuthTokenProvider } from "@memorang/ui/contexts/AuthTokenContext";
import { GestureHandlerRootHOC } from "components/GestureHandlerRootHOC";
import MaintenanceViewContainer from "components/MaintenanceViewContainer";
import VersionCheckerProvider from "./providers/VersionChecker";

/* ----------------- Components ----------------- */
import Loader from "@memorang/ui/components/Loader";

/* ----------------- Bootstrap ----------------- */

let hasBootstrapped = false;
const enableLogging = false;

const bootstrapApp = () => {
	if (hasBootstrapped) {
		return;
	}
	hasBootstrapped = true;

	applyGlobalPolyfills();
	initSentry();
	initializeAnalytics();

	if (!enableLogging) {
		LogBox.ignoreAllLogs();
	}
};

bootstrapApp();

/* ----------------- Clerk token cache ----------------- */

const tokenCache = {
	getToken: async (key: string) => {
		try {
			return await SecureStore.getItemAsync(key);
		} catch (error) {
			console.error("SecureStore get item error:", error);

			try {
				await SecureStore.deleteItemAsync(key);
			} catch {
				// noop
			}

			return null;
		}
	},

	saveToken: async (key: string, value: string) => {
		try {
			await SecureStore.setItemAsync(key, value);
		} catch (error) {
			console.error("SecureStore set item error:", error);
		}
	},
};

/* ----------------- Small internal pieces ----------------- */

const AppNavigator = () => (
	<Stack
		screenOptions={{
			headerShown: false,
		}}
	/>
);

const WebHead = () => {
	if (!isWeb) {
		return null;
	}

	return (
		<Head>
			<link
				rel="shortcut icon"
				href={`https://services.memorang.com/storage/v1/object/public/memorang-assets/icons/${buildType}/icon.png`}
			/>
		</Head>
	);
};

const AppDataBootstrap = () => {
	useProductMetadata("STUDY_PACK");
	useProductMetadata("PRACTICE_TEST");
	useProductDetails("PRACTICE_TEST");
	useProductDetails("STUDY_PACK");
	useExams();

	return null;
};

const InitialLoader = ({ isDarkMode }: { isDarkMode: boolean }) => (
	<ThemeContextProvider initIsDarkMode={isDarkMode}>
		<PaperProvider theme={theme(isDarkMode) as unknown as ThemeProp}>
			<Loader />
		</PaperProvider>
	</ThemeContextProvider>
);

const OptionalClerkProvider = ({
	children,
	enabled,
}: {
	children: React.ReactNode;
	enabled: boolean;
}) => {
	if (!enabled) {
		return <>{children}</>;
	}

	const publishableKey = getClerkPublishableKey();

	return (
		<ClerkProvider tokenCache={tokenCache} publishableKey={publishableKey}>
			<ClerkLoaded>{children}</ClerkLoaded>
		</ClerkProvider>
	);
};

const FeatureProviders = ({ children }: { children: React.ReactNode }) => {
	const appId = useAppStore((store) => store.app?.id);
	const org = useAppStore((store) => store.companyName);
	const tenantId = useAppStore((store) => store.tenant?.id);
	const appName = useAppStore((store) => store.app?.name);

	return (
		<ToastProvider>
			<VersionCheckerProvider>
				<HapticProvider>
					<TTSProvider
						org={org}
						tenantId={tenantId}
						appId={appId}
						appName={appName}
					>
						<BottomSheetModalProvider>{children}</BottomSheetModalProvider>
					</TTSProvider>
				</HapticProvider>
			</VersionCheckerProvider>
		</ToastProvider>
	);
};

const AppShell = () => {
	const { isDarkMode } = useThemeContext();
	const { isLoaded } = useAuthUser();

	const finalTheme = theme(isDarkMode);

	return (
		<AuthTokenProvider value={getAuthToken}>
			<PaperProvider theme={finalTheme as unknown as ThemeProp}>
				<NavigationThemeProvider
					value={{
						...finalTheme,
						fonts: {
							...finalTheme.typography.fonts,
							heavy: finalTheme.typography.fonts.extraBold,
							// biome-ignore lint/correctness/noUndeclaredVariables: <explanation>
						} as ReactNavigation.Theme["fonts"],
					}}
				>
					<StatusBar
						key={isDarkMode ? "dark" : "light"}
						style={isDarkMode ? "light" : "dark"}
					/>

					<AppDataBootstrap />

					{isLoaded ? (
						<FeatureProviders>
							<AppNavigator />
						</FeatureProviders>
					) : (
						<Loader />
					)}
				</NavigationThemeProvider>
			</PaperProvider>
		</AuthTokenProvider>
	);
};

const MaintenanceShell = ({ isDarkMode }: { isDarkMode: boolean }) => (
	<ThemeContextProvider initIsDarkMode={isDarkMode}>
		<PaperProvider theme={theme(isDarkMode) as unknown as ThemeProp}>
			<MaintenanceViewContainer />
		</PaperProvider>
	</ThemeContextProvider>
);

const AppEntry = () => {
	const { isLoadingComplete, isDarkMode } = useCachedResources();

	const appId = useAppStore((store) => store.app?.id);
	const authProvider = useAppStore((store) => store.app?.auth?.provider);
	const maintenanceEnabled = useAppStore(
		(store) => store.app?.maintenance?.enabled,
	);
	const useClerk = authProvider === "clerk";

	if (!(appId && isLoadingComplete)) {
		return <InitialLoader isDarkMode={isDarkMode} />;
	}

	if (maintenanceEnabled) {
		return <MaintenanceShell isDarkMode={isDarkMode} />;
	}

	return (
		<>
			<WebHead />
			<OptionalClerkProvider enabled={useClerk}>
				<GestureHandlerRootHOC>
					<ThemeContextProvider initIsDarkMode={isDarkMode}>
						<ReactQueryProvider>
							<AuthProvider
								app={buildType}
								identifyUser={identifyUser}
								appId={appId}
							>
								<ExamContextProvider>
									<ConsentProvider>
										<AppShell />
									</ConsentProvider>
								</ExamContextProvider>
							</AuthProvider>
						</ReactQueryProvider>
					</ThemeContextProvider>
				</GestureHandlerRootHOC>
			</OptionalClerkProvider>
		</>
	);
};

const AppRouter = () => {
	const isHealthRoute =
		isWeb &&
		typeof window !== "undefined" &&
		window.location.pathname.startsWith("/health");

	if (isHealthRoute) {
		return <AppNavigator />;
	}

	return <AppEntry />;
};

export default isWeb ? AppRouter : SentryNative.wrap(AppRouter);
